import { useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  formatMoney,
  monthLabel,
  moscowMonth,
  type PayrollList,
  recentMonths,
  type SalaryImportResult,
  uploadSalaries,
  usePayrollList,
  useSaveHolidays,
} from '../api/payroll';
import { formatIsoDate } from '../api/records';
import { useCurrentUser } from '../api/session';
import { Avatar } from '../components/Avatar';
import { downloadCsv } from '../components/csv';
import { Icon } from '../components/Icon';
import { KpiCard } from '../components/Stats';
import { EmptyState, ErrorState, LoadingState } from '../components/states';
import { usePageTitle } from '../layout/shellContext';

/** Загрузка окладов файлом: шаблон со всеми сотрудниками, ID Bitrix24 уже проставлены. */
function SalaryUpload({ data, month }: { data: PayrollList; month: string }) {
  const inputId = useId();
  const queryClient = useQueryClient();
  const [from, setFrom] = useState(`${month}-01`);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SalaryImportResult | null>(null);
  const [error, setError] = useState<unknown>(null);

  const template = () =>
    downloadCsv(
      'оклады.csv',
      ['ФИО', 'должность', 'bitrix_user_id', 'оклад', 'валюта', 'KPI, %', 'с даты'],
      data.people
        .filter((person) => person.bitrixId)
        .map((person) => [
          person.fullName,
          person.position,
          person.bitrixId,
          person.salary ? person.salary.amountMinor / 100 : '',
          person.salary?.currency ?? 'RUB',
          person.salary?.kpiPercent ?? '',
          '',
        ]),
    );

  return (
    <section className="panel">
      <div className="panel-header">
        <h2>Оклады файлом</h2>
        <button type="button" className="btn-secondary" onClick={template}>
          <Icon name="download" size={14} />
          Шаблон со всеми сотрудниками
        </button>
      </div>
      <p className="muted">
        Заполните «оклад» (валюта RUB или AMD) и «KPI, %» — премию за KPI в процентах от оклада — и загрузите
        файл. Пустая «с даты» — оклад действует с даты ниже; пустой «KPI, %» — процент остаётся прежним.
      </p>
      <div className="payroll-page-tools">
        <label className="field">
          <span>Оклад действует с</span>
          <input
            className="input"
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </label>
        <label htmlFor={inputId} className={`btn-primary${busy ? ' disabled' : ''}`}>
          <Icon name="upload" size={14} />
          {busy ? 'Загружаю…' : 'Загрузить файл'}
        </label>
        <input
          id={inputId}
          className="visually-hidden"
          type="file"
          accept=".xlsx,.csv"
          aria-label="Файл окладов"
          disabled={busy}
          onChange={async (event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (!file || !from) return;
            setBusy(true);
            setError(null);
            setResult(null);
            try {
              setResult(await uploadSalaries(file, from));
              await queryClient.invalidateQueries({ queryKey: ['payroll-list'] });
            } catch (caught) {
              setError(caught);
            } finally {
              setBusy(false);
            }
          }}
        />
      </div>
      {error ? <ErrorState title="Файл не загружен" error={error} /> : null}
      {result ? (
        <div className={`notice${result.errors.length ? '' : ' notice-ok'}`}>
          <Icon name={result.errors.length ? 'alert' : 'check'} size={14} />
          <div>
            Сохранено окладов: {result.saved}.
            {result.errors.length ? (
              <ul className="import-errors">
                {result.errors.map((item) => (
                  <li key={item.row}>
                    Строка {item.row}: {item.message}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}

/** Нерабочие дни компании: праздники и переносы. В них нет ни прогулов, ни опозданий. */
function Holidays({ items, canEdit }: { items: { day: string; title: string }[]; canEdit: boolean }) {
  const [draft, setDraft] = useState<{ day: string; title: string }[] | null>(null);
  const save = useSaveHolidays();
  const rows = draft ?? items;
  const valid = rows.every((item) => item.day && item.title.trim());
  return (
    <section className="panel">
      <div className="panel-header">
        <h2>Нерабочие дни</h2>
        {canEdit && !draft ? (
          <button type="button" className="btn-secondary" onClick={() => setDraft(items)}>
            <Icon name="pen" size={14} />
            Изменить
          </button>
        ) : null}
      </div>
      <p className="muted">
        Праздники и переносы выходных. Они не считаются рабочими: прогулов нет, и дневная ставка считается без
        них.
      </p>
      {draft ? (
        <div className="holiday-rows">
          {draft.map((item, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: строки черновика без своих ключей
            <div key={index} className="holiday-row">
              <input
                className="input"
                type="date"
                aria-label="Дата"
                value={item.day}
                onChange={(event) =>
                  setDraft(draft.map((row, i) => (i === index ? { ...row, day: event.target.value } : row)))
                }
              />
              <input
                className="input"
                aria-label="Название"
                placeholder="Например, День народного единства"
                value={item.title}
                onChange={(event) =>
                  setDraft(draft.map((row, i) => (i === index ? { ...row, title: event.target.value } : row)))
                }
              />
              <button
                type="button"
                className="btn-icon"
                aria-label="Удалить день"
                onClick={() => setDraft(draft.filter((_, i) => i !== index))}
              >
                <Icon name="trash" size={15} />
              </button>
            </div>
          ))}
          <div className="salary-form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDraft([...draft, { day: '', title: '' }])}
            >
              <Icon name="plus" size={14} />
              Добавить день
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={!valid || save.isPending}
              onClick={() => save.mutate(draft, { onSuccess: () => setDraft(null) })}
            >
              Сохранить
            </button>
            <button type="button" className="btn-secondary" onClick={() => setDraft(null)}>
              Отмена
            </button>
          </div>
          {save.isError ? <ErrorState title="Не сохранено" error={save.error} /> : null}
        </div>
      ) : items.length ? (
        <ul className="payroll-absence-list">
          {items.map((item) => (
            <li key={item.day} className="absence-row">
              <strong>{formatIsoDate(item.day)}</strong>
              <span>{item.title}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">Не заданы: рабочими считаются все будни.</p>
      )}
    </section>
  );
}

/** «Оклад и KPI» сотрудников за месяц: для руководителей, HR и администратора. */
export function PayrollPage() {
  usePageTitle('Оклад и KPI');
  const navigate = useNavigate();
  const me = useCurrentUser();
  const current = moscowMonth();
  const [month, setMonth] = useState(current);
  const [query, setQuery] = useState('');
  const list = usePayrollList(month);

  if (list.isPending) return <LoadingState label="Считаю удержания" />;
  if (list.isError) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  const data = list.data;
  if (data.mode === 'none')
    return (
      <div className="page">
        <section className="panel">
          <EmptyState title="Оклады вам не видны">
            Здесь руководители видят оклады и KPI своих сотрудников, HR и администратор — всех. Свой оклад
            смотрите в «Моём кабинете».
          </EmptyState>
        </section>
      </div>
    );

  const needle = query.trim().toLowerCase();
  const people = needle
    ? data.people.filter((person) =>
        [person.fullName, person.position, person.city].some((value) =>
          value?.toLowerCase().includes(needle),
        ),
      )
    : data.people;
  const withSalary = data.people.filter((person) => person.salary);
  const currencies = new Set(withSalary.map((person) => person.salary?.currency));
  const single = currencies.size === 1 ? ([...currencies][0] ?? null) : null;
  const deducted = data.people.reduce((sum, person) => sum + person.totals.deductedMinor, 0);
  const openCabinet = (id: string) =>
    navigate(id === me.data?.employeeId ? '/me#payroll' : `/employees/${id}#payroll`);

  return (
    <div className="page">
      <section className="panel period-bar">
        <fieldset className="pill-row period-pills">
          <legend className="visually-hidden">Месяц</legend>
          {recentMonths(current).map((item) => (
            <button
              key={item}
              type="button"
              className="pill-toggle"
              aria-pressed={item === month}
              onClick={() => setMonth(item)}
            >
              {monthLabel(item)}
            </button>
          ))}
        </fieldset>
        <input
          className="input payroll-search"
          type="search"
          aria-label="Поиск сотрудника"
          placeholder="ФИО, должность или город"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </section>

      <div className="kpi-grid">
        <KpiCard label="Сотрудников" icon="users" value={data.people.length} />
        <KpiCard
          label="С окладом"
          icon="wallet"
          value={withSalary.length}
          extra={
            withSalary.length < data.people.length
              ? `без оклада ${data.people.length - withSalary.length}`
              : undefined
          }
        />
        <KpiCard
          label="Удержано за месяц"
          icon="alert"
          tone={deducted ? 'bad' : undefined}
          value={single || !deducted ? formatMoney(deducted, single) : 'в разных валютах'}
        />
        <KpiCard
          label="Прогулов"
          icon="calendar"
          value={data.people.reduce((sum, person) => sum + person.totals.absent, 0)}
          extra={`опозданий ${data.people.reduce((sum, person) => sum + person.totals.late, 0)}`}
        />
      </div>

      <section className="panel">
        <div className="panel-header">
          <h2>Сотрудники · {monthLabel(month)}</h2>
        </div>
        {people.length ? (
          <div className="table-scroll">
            <table className="metrics-table payroll-list">
              <thead>
                <tr>
                  <th>Сотрудник</th>
                  <th className="number">Оклад</th>
                  <th className="number">KPI, %</th>
                  <th className="number">Выполнение KPI</th>
                  <th className="number">Премия за KPI</th>
                  <th className="number">Удержания и нарушения</th>
                  <th className="number">Итого</th>
                </tr>
              </thead>
              <tbody>
                {people.map((person) => {
                  const currency = person.salary?.currency ?? null;
                  const withheld = person.totals.deductedMinor + (person.finesMinor ?? 0);
                  return (
                    <tr key={person.id} className="clickable" onClick={() => openCabinet(person.id)}>
                      <td>
                        <button
                          type="button"
                          className="person-line row-link"
                          onClick={(event) => {
                            event.stopPropagation();
                            openCabinet(person.id);
                          }}
                        >
                          <Avatar name={person.fullName} photoUrl={person.photoUrl} />
                          <span className="person-line-text">
                            <b>{person.fullName}</b>
                            <small>
                              {[person.position, person.city].filter(Boolean).join(' · ')}
                              {!person.linked
                                ? ' · нет в системе проходов'
                                : !person.hasSchedule
                                  ? ' · нет графика'
                                  : ''}
                            </small>
                          </span>
                        </button>
                      </td>
                      <td className="number">
                        {person.salary ? (
                          formatMoney(person.salary.amountMinor, person.salary.currency)
                        ) : (
                          <span className="muted">не назначен</span>
                        )}
                      </td>
                      <td className="number">
                        {person.salary?.kpiPercent !== null && person.salary?.kpiPercent !== undefined
                          ? `${person.salary.kpiPercent}`
                          : '—'}
                      </td>
                      <td className="number">
                        {person.kpi?.total !== null && person.kpi?.total !== undefined
                          ? `${person.kpi.total} %`
                          : '—'}
                      </td>
                      <td className="number">
                        {person.kpi?.premiumMinor ? formatMoney(person.kpi.premiumMinor, currency) : '—'}
                      </td>
                      <td className={`number${withheld ? ' bad' : ''}`}>
                        {withheld ? formatMoney(withheld, currency) : '—'}
                      </td>
                      <td className="number">
                        {person.totalMinor !== null ? formatMoney(person.totalMinor, currency) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="Никого не нашлось" />
        )}
      </section>

      {data.canManage ? <SalaryUpload data={data} month={month} /> : null}
      <Holidays items={data.holidays} canEdit={data.canManage} />
    </div>
  );
}
